/**
 * Share dispatcher + share-content builders + lineup pick encoding.
 *
 * Dispatch cascade:
 *   1) Capacitor native (iOS/Android WebView) → Capacitor Share plugin
 *      → OS share sheet (IG Stories, Snap, WhatsApp, Messages, etc.)
 *   2) Web Share API (`navigator.share`) on modern mobile browsers
 *   3) Caller falls back to the popover in ShareButton (X/FB/LinkedIn/copy)
 */
import { api, type ArtistDTO, type EventDTO } from "./api";
import type { EventPrefs, PreferenceMap, Rating, SetPreferenceMap } from "./preferences";
import { trackEvent } from "./analytics";
import { getDeviceId } from "./device";

export type ShareContent = {
  title: string;
  text: string;
  url: string;
};

export type ShareResult = "native" | "fallback-needed" | "cancelled" | "error";

// ---------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------

function isCapacitor(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } };
  return !!w.Capacitor?.isNativePlatform?.();
}

export function canNativeShare(): boolean {
  if (typeof navigator === "undefined") return false;
  if (isCapacitor()) return true;
  return typeof navigator.share === "function";
}

export async function doShare(content: ShareContent): Promise<ShareResult> {
  trackEvent("share_attempt", { url: content.url });

  // Capacitor native path
  if (isCapacitor()) {
    try {
      const mod = await import("@capacitor/share");
      await mod.Share.share({
        title: content.title,
        text: content.text,
        url: content.url,
        dialogTitle: "Share via",
      });
      trackEvent("share_complete", { channel: "native", url: content.url });
      return "native";
    } catch (err) {
      // User cancelled — Capacitor throws on cancel.
      const m = (err as Error)?.message?.toLowerCase() ?? "";
      if (m.includes("cancel") || m.includes("abort")) return "cancelled";
      return "error";
    }
  }

  // Web Share API (mobile browsers + recent desktop Safari/Chrome with https)
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title: content.title, text: content.text, url: content.url });
      trackEvent("share_complete", { channel: "web_share", url: content.url });
      return "native";
    } catch (err) {
      const m = (err as Error)?.message?.toLowerCase() ?? "";
      if (m.includes("abort") || m.includes("cancel")) return "cancelled";
      return "error";
    }
  }

  // Caller must show the popover.
  return "fallback-needed";
}

// Intent URLs for the fallback popover.
export function twitterIntentUrl(c: ShareContent): string {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(c.text)}&url=${encodeURIComponent(c.url)}`;
}
export function facebookShareUrl(c: ShareContent): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(c.url)}&quote=${encodeURIComponent(c.text)}`;
}
export function linkedInShareUrl(c: ShareContent): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(c.url)}`;
}
export function whatsappShareUrl(c: ShareContent): string {
  return `https://wa.me/?text=${encodeURIComponent(`${c.text} ${c.url}`)}`;
}
export function telegramShareUrl(c: ShareContent): string {
  return `https://t.me/share/url?url=${encodeURIComponent(c.url)}&text=${encodeURIComponent(c.text)}`;
}
export function mailtoUrl(c: ShareContent): string {
  return `mailto:?subject=${encodeURIComponent(c.title)}&body=${encodeURIComponent(`${c.text}\n\n${c.url}`)}`;
}

export async function copyLink(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    trackEvent("share_complete", { channel: "copy_link", url });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------
// Origin resolution (prefer custom domain)
// ---------------------------------------------------------------

export function shareOrigin(): string {
  if (typeof window === "undefined") return "https://checkthelineup.com";
  // Native WebView uses capacitor://localhost — always point share URLs
  // at the public site instead.
  if (isCapacitor()) return "https://checkthelineup.com";
  return window.location.origin;
}

// ---------------------------------------------------------------
// Hashtag derivation
// ---------------------------------------------------------------

const EVENT_HASHTAGS: Record<string, string> = {
  edc_las_vegas_2026: "EDCLV2026",
  lib_2026: "LIB2026",
  coachella_2026: "Coachella2026",
  ubbi_dubbi_2026: "UbbiDubbi2026",
  forbidden_kingdom_2026: "ForbiddenKingdom2026",
  movement_2026: "Movement2026",
  electric_forest_2026: "ElectricForest2026",
  beyond_wonderland_gorge_2026: "BeyondWonderland2026",
  hard_summer_2026: "HARDSummer2026",
  bass_canyon_2026: "BassCanyon2026",
  lost_lands_2026: "LostLands2026",
};

export function hashtagsFor(eventSlug: string): string[] {
  const event = EVENT_HASHTAGS[eventSlug];
  return event ? [event, "CheckTheLineUp"] : ["CheckTheLineUp"];
}

function tagString(slugs: string[]): string {
  return slugs.map(s => `#${s}`).join(" ");
}

// ---------------------------------------------------------------
// Share templates
// ---------------------------------------------------------------

export function shareForArtist(artist: ArtistDTO, event: EventDTO): ShareContent {
  const set = artist.sets[0];
  const stage = set?.stage.name ?? "the lineup";
  // Only include the time if there's a real one — empty startLabel means the
  // festival hasn't published official set times yet (see queries.ts).
  const when = set
    ? set.startLabel
      ? `Day ${set.day} · ${set.startLabel}`
      : `Day ${set.day}`
    : "";
  const body = set
    ? `Catching ${artist.name} at ${event.name} — ${stage}, ${when}.`
    : `${artist.name} on the ${event.name} lineup.`;
  return {
    title: `${artist.name} · ${event.name}`,
    text: `${body} ${tagString(hashtagsFor(event.slug))}`.trim(),
    url: `${shareOrigin()}/events/${encodeURIComponent(event.slug)}/artist/${encodeURIComponent(artist.slug)}`,
  };
}

export function shareForEvent(event: EventDTO): ShareContent {
  return {
    title: `${event.name} · CheckTheLineUp`,
    text: `I'm going to ${event.name}. Building my lineup on CheckTheLineUp. ${tagString(hashtagsFor(event.slug))}`,
    url: `${shareOrigin()}/events/${encodeURIComponent(event.slug)}`,
  };
}

/**
 * Mints a server-side short-URL token for these picks, returning a share
 * content bundle with a tiny /s/ABC23QK5 URL.
 *
 * Falls back to the legacy ?picks=... query-param encoding when offline
 * or when the API rejects the request — so sharing still works with no
 * network.
 */
/**
 * Mints a share for a full `EventPrefs` snapshot — artists + per-set
 * overrides. Both are roundtripped to the recipient so a curator-mode
 * schedule (sets only, no artist-level likes) imports correctly.
 *
 * Accepts either shape:
 *  - `EventPrefs` ({ artists, sets }) — new callers, includes set picks
 *  - `PreferenceMap` (flat slug→rating) — legacy callers, treated as artists only
 */
/** How many set-rows we dump into the share text before collapsing the rest. */
const MAX_LIST_LINES = 25;

export async function shareForLineup(
  event: EventDTO,
  prefsOrEvent: PreferenceMap | EventPrefs,
  opts: { name?: string | null } = {},
): Promise<ShareContent> {
  const normalized = normalizeToEventPrefs(prefsOrEvent);
  const totalPicks = countEventPrefsPicks(normalized);
  const named = opts.name?.trim() || null;
  const title = named ? `${named} · ${event.name}` : `My ${event.name} lineup`;

  // Skip the API hop entirely when there's nothing to share.
  if (totalPicks === 0) {
    const text = named
      ? `My ${event.name} lineup — ${named} (0 picks). ${tagString(hashtagsFor(event.slug))}`
      : `My ${event.name} lineup — nothing picked yet. ${tagString(hashtagsFor(event.slug))}`;
    return {
      title,
      text,
      url: `${shareOrigin()}/events/${encodeURIComponent(event.slug)}/schedule`,
    };
  }

  // Resolve picks into human-readable "Artist · Day Time · Stage" lines.
  // Uses the in-memory snapshot so it's instant; only hits the network on
  // a cold app install.
  const listing = await buildLineupListing(event.slug, normalized);
  const header = named
    ? `My ${event.name} lineup — ${named}:`
    : `My ${event.name} lineup:`;
  const text =
    `${header}\n${listing}\n${tagString(hashtagsFor(event.slug))}`.trim();

  // Build the API payload — filtered to just like/superlike.
  const artistPayload: Record<string, "like" | "superlike"> = {};
  for (const [slug, r] of Object.entries(normalized.artists)) {
    if (r === "like" || r === "superlike") artistPayload[slug] = r;
  }
  const setPayload: Record<number, "like" | "superlike"> = {};
  for (const [k, r] of Object.entries(normalized.sets)) {
    if (r === "like" || r === "superlike") setPayload[Number(k)] = r;
  }
  try {
    const { url: path } = await api.createShare(
      event.slug,
      { artists: artistPayload, sets: setPayload },
      named,
      getDeviceId(),
    );
    return { title, text, url: `${shareOrigin()}${path}` };
  } catch {
    // Offline or API sad — fall back to the legacy ?picks= URL. Sets
    // aren't representable in that encoding, so only artist-level picks
    // roundtrip; the recipient will only import artist likes.
    const encoded = encodePicks(normalized.artists);
    const url =
      `${shareOrigin()}/events/${encodeURIComponent(event.slug)}/schedule` +
      (encoded ? `?picks=${encodeURIComponent(encoded)}` : "");
    return { title, text, url };
  }
}

/**
 * Turn {artists, sets} picks into a newline-separated listing, each line
 * like "• Porter Robinson · D1 8:00 PM · Lightning".
 *
 * Resolution rules:
 *  - Artist-level picks → every set that artist plays at the event.
 *  - Set-level picks → just that specific set, looked up by id.
 *  - Dedupe by set id.
 *  - Sort by day asc, then start time asc.
 *  - Cap at MAX_LIST_LINES; beyond that append "… and N more".
 *  - Superlikes get a ★ prefix.
 */
async function buildLineupListing(eventSlug: string, prefs: EventPrefs): Promise<string> {
  let artists: ArtistDTO[] = [];
  try {
    const raw = await api.artists(eventSlug);
    if (Array.isArray(raw)) artists = raw;
  } catch {
    // Network/snapshot not available — caller falls back to bare count text.
  }
  if (artists.length === 0) return "";

  type Item = {
    setId: number;
    day: number;
    start: number;
    startLabel: string;
    stage: string;
    artistName: string;
    rating: "like" | "superlike";
  };

  const items = new Map<number, Item>();

  // Artist-level picks expand to all of that artist's sets.
  for (const [slug, rating] of Object.entries(prefs.artists)) {
    if (rating !== "like" && rating !== "superlike") continue;
    const a = artists.find(x => x.slug === slug);
    if (!a) continue;
    for (const s of a.sets) {
      items.set(s.id, {
        setId: s.id,
        day: s.day,
        start: s.start,
        startLabel: s.startLabel,
        stage: s.stage.name,
        artistName: a.name,
        rating,
      });
    }
  }

  // Set-level picks override (or add) per-set — curator mode lands here.
  for (const [idStr, rating] of Object.entries(prefs.sets)) {
    if (rating !== "like" && rating !== "superlike") continue;
    const setId = Number(idStr);
    if (!Number.isFinite(setId)) continue;
    // Find the owning artist/set.
    let match: { a: ArtistDTO; s: ArtistDTO["sets"][number] } | null = null;
    for (const a of artists) {
      const s = a.sets.find(x => x.id === setId);
      if (s) { match = { a, s }; break; }
    }
    if (!match) continue;
    items.set(setId, {
      setId,
      day: match.s.day,
      start: match.s.start,
      startLabel: match.s.startLabel,
      stage: match.s.stage.name,
      artistName: match.a.name,
      rating,
    });
  }

  const sorted = [...items.values()].sort((a, b) => a.day - b.day || a.start - b.start);
  if (sorted.length === 0) return "";

  const shown = sorted.slice(0, MAX_LIST_LINES);
  const lines = shown.map(i => {
    const mark = i.rating === "superlike" ? "★" : "•";
    const when = i.startLabel ? ` ${i.startLabel}` : "";
    return `${mark} ${i.artistName} · D${i.day}${when} · ${i.stage}`;
  });
  if (sorted.length > shown.length) {
    lines.push(`… and ${sorted.length - shown.length} more`);
  }
  return lines.join("\n");
}

function normalizeToEventPrefs(input: PreferenceMap | EventPrefs): EventPrefs {
  if (input && typeof input === "object" && ("artists" in input || "sets" in input)) {
    const ep = input as EventPrefs;
    return {
      artists: ep.artists ?? {},
      sets: ep.sets ?? {},
    };
  }
  return { artists: (input as PreferenceMap) ?? {}, sets: {} };
}

function countEventPrefsPicks(ep: EventPrefs): number {
  let n = 0;
  for (const r of Object.values(ep.artists)) if (r === "like" || r === "superlike") n++;
  for (const r of Object.values(ep.sets)) if (r === "like" || r === "superlike") n++;
  return n;
}

// ---------------------------------------------------------------
// Lineup pick encode / decode
// ---------------------------------------------------------------

/**
 * Compact encoding of a user's picks for URL transport. Only like +
 * superlike are shared (pass / meh are private). Format:
 *
 *   v1:slug-one,slug-two!,slug-three
 *
 * Trailing `!` = superlike; no suffix = like.
 */
export function encodePicks(prefs: PreferenceMap): string {
  const parts: string[] = [];
  for (const [slug, r] of Object.entries(prefs)) {
    if (r === "superlike") parts.push(`${slug}!`);
    else if (r === "like") parts.push(slug);
  }
  if (parts.length === 0) return "";
  return `v1:${parts.join(",")}`;
}

export function decodePicks(encoded: string | null | undefined): PreferenceMap | null {
  if (!encoded) return null;
  if (!encoded.startsWith("v1:")) return null;
  const body = encoded.slice(3);
  if (!body) return {};
  const out: PreferenceMap = {};
  for (const raw of body.split(",")) {
    const token = raw.trim();
    if (!token) continue;
    if (token.endsWith("!")) {
      const s = token.slice(0, -1);
      if (s) out[s] = "superlike";
    } else {
      out[token] = "like";
    }
  }
  return out;
}

export function countPicks(prefs: PreferenceMap): number {
  let n = 0;
  for (const r of Object.values(prefs)) {
    if (r === "like" || r === "superlike") n++;
  }
  return n;
}

/** Used by tests to assert every rating value is accounted for. */
export const ALL_RATINGS: readonly Rating[] = ["pass", "meh", "like", "superlike"] as const;
